import { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { Plus, Pencil, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import type { Actor, ProjectContext } from "@iamu/shared";

const COMMON_ACTORS: Array<{ name: string; role: string }> = [
  {
    name: "End User / Customer",
    role: "Primary consumer navigating products, completing checkout, or managing account settings.",
  },
  {
    name: "System Administrator",
    role: "Internal operator configuring organization settings, roles, integrations, and permissions.",
  },
  {
    name: "Compliance / Safety Officer",
    role: "Auditor reviewing regulatory compliance, export logs, incident reports, and retention policies.",
  },
];

export interface ActorsSectionProps {
  context: ProjectContext;
  onUpdate: (field: string, value: unknown) => void;
  canEdit: boolean;
}

export function ActorsSection({
  context,
  onUpdate,
  canEdit,
}: ActorsSectionProps) {
  const actors = context.actors ?? [];
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  function handleSaveActor(actor: Actor) {
    let updated: Actor[];
    if (editingIndex !== null) {
      updated = actors.map((a, i) => (i === editingIndex ? actor : a));
    } else {
      updated = [...actors, actor];
    }
    onUpdate("actors", updated);
    setIsDialogOpen(false);
    setEditingIndex(null);
  }

  function handleAddPreset(preset: (typeof COMMON_ACTORS)[number]) {
    if (
      actors.some((a) => a.name.toLowerCase() === preset.name.toLowerCase())
    ) {
      toast.info("Actor already added");
      return;
    }
    const updated = [...actors, preset];
    onUpdate("actors", updated);
    toast.success(`Added ${preset.name.split(" /")[0]}`);
  }

  function removeActor(index: number) {
    onUpdate(
      "actors",
      actors.filter((_, i) => i !== index),
    );
  }

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-primary" />
            <CardTitle className="text-base">
              Key actors & stakeholder roles
            </CardTitle>
          </div>
          <CardDescription>
            Real-world human roles interacting with your domain. These ground
            walkthrough runner personas.
          </CardDescription>
        </div>
        {canEdit && (
          <Button
            variant="outline"
            size="sm"
            className="shrink-0"
            onClick={() => {
              setEditingIndex(null);
              setIsDialogOpen(true);
            }}
          >
            <Plus className="mr-1 h-3.5 w-3.5" />
            Add
          </Button>
        )}
      </CardHeader>
      <CardContent className="flex-1">
        {actors.length === 0 ? (
          <div className="py-6 text-center space-y-3">
            <p className="text-sm text-muted-foreground">
              No actors defined yet.
            </p>
            {canEdit && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Quick-add common stakeholder roles:
                </p>
                <div className="flex flex-wrap justify-center gap-1.5">
                  {COMMON_ACTORS.map((preset) => (
                    <Button
                      key={preset.name}
                      variant="outline"
                      size="xs"
                      onClick={() => handleAddPreset(preset)}
                    >
                      <Plus className="h-3 w-3" />
                      {preset.name.split(" /")[0]}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="divide-y divide-border/60">
            {actors.map((actor, i) => (
              <div
                key={i}
                className="flex items-start justify-between gap-3 py-2.5"
              >
                <div className="min-w-0 flex-1 space-y-0.5">
                  <p className="text-sm font-medium text-foreground">
                    {actor.name}
                  </p>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {actor.role}
                  </p>
                </div>
                {canEdit && (
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setEditingIndex(i);
                        setIsDialogOpen(true);
                      }}
                      aria-label={`Edit actor ${actor.name}`}
                    >
                      <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                    </Button>
                    <SimpleTooltip
                      content={`Remove actor ${actor.name || i + 1}`}
                    >
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeActor(i)}
                        aria-label={`Remove actor ${actor.name || i + 1}`}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                      </Button>
                    </SimpleTooltip>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {canEdit && isDialogOpen && (
        <ActorDialog
          initialActor={
            editingIndex !== null ? actors[editingIndex] : undefined
          }
          onSave={handleSaveActor}
          onClose={() => {
            setIsDialogOpen(false);
            setEditingIndex(null);
          }}
        />
      )}
    </Card>
  );
}

interface ActorDialogProps {
  initialActor?: Actor;
  onSave: (actor: Actor) => void;
  onClose: () => void;
}

function ActorDialog({ initialActor, onSave, onClose }: ActorDialogProps) {
  const [name, setName] = useState(initialActor?.name ?? "");
  const [role, setRole] = useState(initialActor?.role ?? "");

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {initialActor ? "Edit key actor" : "Add key actor"}
          </DialogTitle>
          <DialogDescription>
            Define a real-world stakeholder or user role that interacts with the
            domain.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="actor-name">Role or actor name</Label>
            <Input
              id="actor-name"
              placeholder="e.g. Safety Inspector, Compliance Officer, Field Operator"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="actor-role">Domain responsibilities</Label>
            <Textarea
              id="actor-role"
              placeholder="What this role does in the real-world domain process (not just UI clicks)..."
              value={role}
              onChange={(e) => setRole(e.target.value)}
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!name.trim()}
            onClick={() => {
              onSave({
                name: name.trim(),
                role: role.trim(),
              });
            }}
          >
            Save actor
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
